import { FC, useState } from "react";
import { Button } from "@/components/UI/Buttons/Button";
import invitationService from "@/services/invitationService";
import { isValidEmail, isPhoneValid } from "@/utils/formValidator";
import PhoneInput from "@/components/UI/PhoneInput";
import { ROLES } from "@/data/interfaces/RolesEnum";
import { ErrorMessage } from "@/components/UI";
import { ResponseError } from "@/data";
import { MEMBERSHIP_ARRAY } from "@/data/membershipProducts";
import { adminControlClass } from "@/components/Account/Shared/adminUi";
import { cn } from "@/lib/utils";

const PATIENT_INVITATION_CITIES = [
  "Vilnius",
  "Kaunas",
  "Šiauliai",
  "Panevėžys",
  "Mažeikiai",
  "Kita",
] as const;

const PAYMENT_INSTALLMENT_OPTIONS = [
  { value: 1, label: "Pilnas mokėjimas" },
  { value: 2, label: "2 mėnesiai" },
  { value: 3, label: "3 mėnesiai" },
] as const;

const invitationFieldClass = cn(
  adminControlClass,
  "inline-flex h-[53px] items-center px-[10px] text-[18px] leading-none",
);

interface InvitationFormProps {
  role: ROLES.DOCTOR | ROLES.USER;
  title: string;
  successMessage: string;
}

const InvitationForm: FC<InvitationFormProps> = ({
  role,
  title,
  successMessage,
}) => {
  const isPatientInvitation = role === ROLES.USER;

  const [formData, setFormData] = useState<{
    name: string;
    surname: string;
    email: string;
    phone: string;
    phoneCountry: string;
    city: string;
    productId: string;
    paymentMonths: number;
    discountPercent: number | null;
  }>({
    name: "",
    surname: "",
    email: "",
    phone: "",
    phoneCountry: "LT",
    city: "",
    productId: "",
    paymentMonths: 1,
    discountPercent: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!formData.name.trim()) {
      setErrorMsg("Įveskite vardą");
      return;
    }
    if (!formData.surname.trim()) {
      setErrorMsg("Įveskite pavardę");
      return;
    }
    if (!formData.email.trim()) {
      setErrorMsg("Įveskite el. paštą");
      return;
    }
    if (!isValidEmail(formData.email)) {
      setErrorMsg("El. pašto formatas netinkamas");
      return;
    }
    if (!formData.phone.trim()) {
      setErrorMsg("Įveskite tel. numerį");
      return;
    }
    if (!isPhoneValid(formData.phone)) {
      setErrorMsg("Neteisingas telefono numerio formatas");
      return;
    }
    if (!isPhoneValid(formData.phone, formData.phoneCountry)) {
      setErrorMsg("Telefono numeris neatitinka pasirinktos šalies");
      return;
    }

    if (isPatientInvitation) {
      if (!formData.city.trim()) {
        setErrorMsg("Pasirinkite miestą");
        return;
      }
      if (!formData.productId) {
        setErrorMsg("Pasirinkite produktą");
        return;
      }

      if (
        !PAYMENT_INSTALLMENT_OPTIONS.some(
          (option) => option.value === formData.paymentMonths,
        )
      ) {
        setErrorMsg("Pasirinkite mokėjimo būdą");
        return;
      }

      if (
        formData.discountPercent !== null &&
        (!Number.isInteger(formData.discountPercent) ||
          formData.discountPercent < 0 ||
          formData.discountPercent > 100)
      ) {
        setErrorMsg("Nuolaida turi būti sveikas skaičius nuo 0 iki 100");
        return;
      }
    }

    setIsLoading(true);
    try {
      if (isPatientInvitation) {
        await invitationService.invitePatient({
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          productId: parseInt(formData.productId),
          paymentMonths: formData.paymentMonths,
          discountPercent: formData.discountPercent ?? 0,
          role,
        });
      } else {
        await invitationService.invite({
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          phone: formData.phone,
          role,
        });
      }
      setSuccessMsg(successMessage);
      setFormData({
        name: "",
        surname: "",
        email: "",
        phone: "",
        phoneCountry: "LT",
        city: "",
        productId: "",
        paymentMonths: 1,
        discountPercent: null,
      });
    } catch (error: unknown) {
      console.error(error);
      if (error instanceof ResponseError) {
        const first = Object.values(error.errors).flat()[0];
        setErrorMsg(first || error.message || "Klaida siunčiant pakvietimą");
      } else if (error instanceof Error) {
        setErrorMsg(error.message || "Klaida siunčiant pakvietimą");
      } else {
        setErrorMsg("Klaida siunčiant pakvietimą");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h3 className="text-ink mb-6 text-2xl font-semibold">{title}</h3>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="text-ink/90 text-lg" htmlFor="name">
            Vardas*
          </label>
          <input
            className={invitationFieldClass}
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-ink/90 text-lg" htmlFor="surname">
            Pavardė*
          </label>
          <input
            className={invitationFieldClass}
            id="surname"
            name="surname"
            value={formData.surname}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-ink/90 text-lg" htmlFor="email">
            El. paštas*
          </label>
          <input
            className={invitationFieldClass}
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-ink/90 text-lg" htmlFor="phone">
            Tel. numeris*
          </label>
          <PhoneInput
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={(val) => {
              setFormData((prev) => ({ ...prev, phone: val }));
              setErrorMsg("");
              setSuccessMsg("");
            }}
            onCountryChange={(c) =>
              setFormData((prev) => ({ ...prev, phoneCountry: c ?? "LT" }))
            }
            disabled={isLoading}
            wrapperClassName="bg-sand h-[53px] rounded-[10px] border-line-strong"
          />
        </div>

        {isPatientInvitation && (
          <>
            <div className="flex flex-col gap-2">
              <label className="text-ink/90 text-lg" htmlFor="city">
                Miestas*
              </label>
              <select
                className={invitationFieldClass}
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Pasirinkite miestą</option>
                {PATIENT_INVITATION_CITIES.map((cityName) => (
                  <option key={cityName} value={cityName}>
                    {cityName}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-ink/90 text-lg" htmlFor="productId">
                Produktas*
              </label>
              <select
                className={invitationFieldClass}
                id="productId"
                name="productId"
                value={formData.productId}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Pasirinkite produktą</option>
                {MEMBERSHIP_ARRAY.map((membership) => (
                  <option key={membership.id} value={membership.id}>
                    {membership.title} - €{membership.firstYearPrice}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-ink/90 text-lg" htmlFor="paymentMonths">
                Mokėjimo būdas*
              </label>
              <select
                className={invitationFieldClass}
                id="paymentMonths"
                name="paymentMonths"
                value={formData.paymentMonths}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    paymentMonths: parseInt(e.target.value, 10),
                  }));
                  setErrorMsg("");
                  setSuccessMsg("");
                }}
                disabled={isLoading}
              >
                {PAYMENT_INSTALLMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-ink/90 text-lg" htmlFor="discountPercent">
                Nuolaida (%)
              </label>
              <input
                className={invitationFieldClass}
                id="discountPercent"
                name="discountPercent"
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                step={1}
                placeholder="0"
                value={formData.discountPercent ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setFormData((prev) => ({ ...prev, discountPercent: null }));
                  } else {
                    const parsed = parseInt(raw, 10);
                    if (Number.isNaN(parsed)) return;
                    setFormData((prev) => ({
                      ...prev,
                      discountPercent: parsed,
                    }));
                  }
                  setErrorMsg("");
                  setSuccessMsg("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "." || e.key === "," || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                disabled={isLoading}
              />
            </div>
          </>
        )}
        {errorMsg && <ErrorMessage error={errorMsg} />}

        {successMsg && (
          <div className="text-green text-center font-medium">{successMsg}</div>
        )}

        <Button
          type="submit"
          variant="ink"
          disabled={isLoading}
          className="rounded-full px-6 py-3"
        >
          {isLoading ? "Siunčiama..." : "Siųsti pakvietimą"}
        </Button>
      </form>
    </div>
  );
};

export default InvitationForm;
